import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  Link as LinkIcon,
  Clock,
  Search,
  SendHorizontal,
  ArrowLeft,
  Package,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import Navbar from '../../components/navbar/navbar'
import apinode from '../../services/apinode'
import swal from 'sweetalert'
import Lottie from 'lottie-react'
import '../pedido-editar/pedido-editar.css'
import animacaoLottie from './animations/animacao.json'
import productNotFoundLottie from './animations/productNotFound.json'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CustomSpinner } from '../../components/CustomSpinner'
import { ContentLoading } from '../../components/ContentLoading'

const locale = ptBR

const T = {
  radius: 10,
  radiusLg: 16,
  radiusXl: 20,
  shadowXs: '0 1px 2px rgba(16, 20, 30, .04)',
  shadowSm: '0 2px 8px rgba(16, 20, 30, .06)',
  shadowMd: '0 8px 24px rgba(16, 20, 30, .10)',
  border: '#E7E8EF',
  borderStrong: '#D9DBE6',
  bgApp: '#F4F5F9',
  bgCard: '#FFFFFF',
  bgSubtle: '#FAFAFC',
  text: '#14151F',
  textSec: '#6B6E7E',
  textMuted: '#9799A8',
  primary: '#4F3FE0',
  primaryDark: '#4232C4',
  primarySoft: '#EFEDFF',
  primarySofter: '#c1baf3',
  ring: 'rgba(79, 63, 224, .18)',
}

const ACCOUNT_PALETTE = [
  { bg: '#EFEDFF', text: '#4F3FE0', border: '#D9D4FF' },
  { bg: '#FFF1E9', text: '#C2540B', border: '#FFD9BE' },
  { bg: '#EAFBF3', text: '#0F8A5F', border: '#BEEED9' },
  { bg: '#FDEEF6', text: '#B32D77', border: '#F9CFE6' },
  { bg: '#EAF3FF', text: '#1D5FD6', border: '#C6DEFF' },
]

function hashToIndex(str, mod) {
  const s = String(str || '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h % mod
}

function accountPalette(label) {
  return ACCOUNT_PALETTE[hashToIndex(label, ACCOUNT_PALETTE.length)]
}

function initialsOf(name) {
  const clean = String(name || '').trim()
  if (!clean) return '?'
  const parts = clean.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function formatBRL(value) {
  if (value == null) return null
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
}

const cardBase = {
  background: T.bgCard,
  border: `1px solid ${T.border}`,
  borderRadius: T.radiusXl,
  boxShadow: T.shadowSm,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}

const S = {
  container: { padding: 0 },
  page: (isMobile) => ({
    display: 'flex',
    height: 'calc(100vh - 64px)',
    background: T.bgApp,
    padding: isMobile ? 8 : 20,
    boxSizing: 'border-box',
    gap: isMobile ? 8 : 16,
    overflow: 'hidden',
  }),
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 'auto',
    textAlign: 'center',
    color: T.textSec,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: T.text,
    margin: '4px 0 6px',
    letterSpacing: '-0.01em',
  },
  emptyLink: {
    fontSize: 13.5,
    color: T.primary,
    fontWeight: 700,
    textDecoration: 'none',
  },
  sidebarWrap: (isMobile, showingChat) => ({
    ...cardBase,
    width: 380,
    minWidth: 320,
    maxWidth: 440,
    display: isMobile ? (showingChat ? 'none' : 'flex') : 'flex',
  }),
  sidebarHeader: {
    background: T.bgCard,
    borderBottom: `1px solid ${T.border}`,
    padding: '18px 20px 14px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  sidebarTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: T.text,
    letterSpacing: '-0.01em',
    margin: 0,
  },
  sidebarSubtitle: { fontSize: 12.5, color: T.textMuted, marginTop: 2 },
  accountInfo: {
    textAlign: 'right',
    fontSize: 11.5,
    color: T.textMuted,
    lineHeight: 1.4,
    paddingTop: 2,
  },
  accountInfoAccent: { fontWeight: 700, color: T.textSec },
  searchWrap: {
    padding: '0 16px 14px',
    background: T.bgCard,
    position: 'sticky',
    top: 62,
    zIndex: 2,
  },
  searchInputWrap: { position: 'relative' },
  searchInput: {
    width: '100%',
    height: 40,
    padding: '0 14px 0 38px',
    borderRadius: 999,
    border: `1px solid ${T.border}`,
    outline: 'none',
    fontSize: 13.5,
    background: T.bgSubtle,
    color: T.text,
  },
  searchIcon: { position: 'absolute', left: 13, top: 11, color: T.textMuted },
  chatList: {
    overflowY: 'auto',
    padding: '4px 10px 12px',
    display: 'grid',
    gap: 4,
    borderTop: `1px solid ${T.border}`,
  },
  chatItem: (active) => ({
    display: 'grid',
    gridTemplateColumns: '42px 1fr',
    gap: 12,
    padding: '11px 10px',
    borderRadius: 14,
    border: '1px solid transparent',
    background: active ? T.primarySofter : 'transparent',
    borderColor: active ? T.primarySoft : 'transparent',
    cursor: 'pointer',
  }),
  avatar: (palette) => ({
    width: 42,
    height: 42,
    borderRadius: 12,
    background: palette.bg,
    color: palette.text,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 13.5,
    flexShrink: 0,
    border: `1px solid ${palette.border}`,
  }),
  itemTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'space-between',
    marginBottom: 3,
  },
  itemTitle: {
    fontWeight: 700,
    fontSize: 13.5,
    color: T.text,
    maxWidth: '68%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemTime: {
    fontSize: 11,
    color: T.textMuted,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  itemChips: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 },
  chatBadge: (palette) => ({
    fontSize: 10.5,
    background: palette.bg,
    color: palette.text,
    padding: '2px 8px',
    borderRadius: 999,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  }),
  itemLastMsg: {
    fontSize: 12.5,
    color: T.textSec,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chatPane: (isMobile, showingChat) => ({
    ...cardBase,
    flex: 1,
    display: isMobile ? (showingChat ? 'flex' : 'none') : 'flex',
  }),
  chatHeader: {
    minHeight: 68,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 20px',
    background: T.bgCard,
    borderBottom: `1px solid ${T.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  backBtn: {
    border: `1px solid ${T.border}`,
    background: T.bgCard,
    padding: 7,
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    color: T.text,
  },
  headerTitleWrap: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  chatHeaderTitle: {
    fontWeight: 800,
    color: T.text,
    fontSize: 15,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chatHeaderSub: {
    fontSize: 12,
    color: T.textSec,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  infoChip: {
    fontSize: 11,
    background: T.bgSubtle,
    border: `1px solid ${T.border}`,
    color: T.textSec,
    padding: '3px 9px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
    fontWeight: 600,
  },
  linkBtn: {
    color: T.primary,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    textDecoration: 'none',
    fontSize: 12,
    fontWeight: 700,
  },
  messagesWrap: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 20px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    background: T.bgApp,
  },
  bubbleRow: (isMine) => ({
    display: 'flex',
    justifyContent: isMine ? 'flex-end' : 'flex-start',
    marginBottom: 10,
  }),
  bubble: (isMine) => ({
    background: isMine ? T.primary : T.bgCard,
    border: isMine ? 'none' : `1px solid ${T.border}`,
    boxShadow: T.shadowXs,
    padding: '11px 14px',
    borderRadius: isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    maxWidth: 600,
    lineHeight: 1.5,
    fontSize: 14,
    color: isMine ? '#FFFFFF' : T.text,
  }),
  bubbleAuthor: (isMine) => ({
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 4,
    color: isMine ? 'rgba(255,255,255,.8)' : T.textMuted,
  }),
  bubbleMeta: (isMine) => ({
    fontSize: 10.5,
    color: isMine ? 'rgba(255,255,255,.72)' : T.textMuted,
    marginTop: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    justifyContent: 'flex-end',
  }),
  productCard: {
    background: T.bgCard,
    border: `1px solid ${T.border}`,
    borderRadius: 16,
    margin: '14px 20px 0',
  },
  productHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '12px 14px',
  },
  productHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  productIconBadge: {
    width: 30,
    height: 30,
    borderRadius: 9,
    background: T.primarySoft,
    color: T.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  productTitle: {
    fontWeight: 700,
    color: T.text,
    fontSize: 13.5,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  productSubGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 8,
    padding: '0 14px 14px',
  },
  productChip: {
    fontSize: 12,
    background: T.bgSubtle,
    border: `1px solid ${T.border}`,
    borderRadius: 10,
    padding: '7px 10px',
    color: T.text,
  },
  ghostBtn: {
    border: `1px solid ${T.border}`,
    background: T.bgCard,
    padding: '7px 12px',
    borderRadius: 999,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 700,
    color: T.text,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  iconGhostBtn: {
    border: `1px solid ${T.border}`,
    background: T.bgCard,
    padding: 7,
    borderRadius: 999,
    cursor: 'pointer',
    color: T.textSec,
    display: 'inline-flex',
    flexShrink: 0,
  },
  attributesTitle: {
    fontSize: 11.5,
    fontWeight: 800,
    color: T.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    margin: '0 14px 8px',
  },
  productLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '4px 14px 14px',
    color: T.textMuted,
    fontSize: 12.5,
  },
  productMiniBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: T.bgCard,
    borderTop: `1px solid ${T.border}`,
    padding: '10px 20px',
  },
  miniThumb: {
    width: 34,
    height: 34,
    borderRadius: 8,
    objectFit: 'cover',
    flexShrink: 0,
  },
  miniTitle: {
    fontSize: 12,
    color: T.textSec,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  inputBar: {
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: T.bgCard,
    borderTop: `1px solid ${T.border}`,
  },
  input: {
    flex: 1,
    border: `1px solid ${T.border}`,
    borderRadius: 999,
    padding: '12px 18px',
    outline: 'none',
    fontSize: 14,
    background: T.bgSubtle,
    color: T.text,
  },
  sendBtn: {
    border: 'none',
    background: T.primary,
    color: '#fff',
    borderRadius: 999,
    padding: '12px 18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontWeight: 700,
    fontSize: 13.5,
    flexShrink: 0,
  },
  sendBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
}

const GlobalStyles = () => (
  <style>{`
    .pgt-chat-item:hover { background: ${T.bgSubtle}; }
    .pgt-search-input:focus, .pgt-msg-input:focus { border-color: ${T.primary} !important; background: #fff !important; box-shadow: 0 0 0 3px ${T.ring}; }
    .pgt-ghost-btn:hover, .pgt-icon-ghost-btn:hover, .pgt-back-btn:hover { background: ${T.bgSubtle}; }
    .pgt-send-btn:not(:disabled):hover { background: ${T.primaryDark}; }
    .pgt-messages-scroll::-webkit-scrollbar, .pgt-list-scroll::-webkit-scrollbar { width: 8px; }
    .pgt-messages-scroll::-webkit-scrollbar-thumb, .pgt-list-scroll::-webkit-scrollbar-thumb { background: ${T.borderStrong}; border-radius: 999px; }
    .pgt-messages-scroll::-webkit-scrollbar-track, .pgt-list-scroll::-webkit-scrollbar-track { background: transparent; }
  `}</style>
)

function MercadoLivreMessages() {
  const [conversations, setConversations] = useState([]) // Lista lateral das conversas
  const [messages, setMessages] = useState([]) // Mensagens do PACK selecionado
  const [isLoading, setIsLoading] = useState(true)
  const [selectedPackId, setSelectedPackId] = useState(null)
  const [response, setResponse] = useState('')
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [showingChat, setShowingChat] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [productDetails, setProductDetails] = useState(null)
  const [productLoading, setProductLoading] = useState(false)
  const [productOpen, setProductOpen] = useState(true)

  const params = useParams()
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth <= 768
      setIsMobile(m)
      if (!m) setShowingChat(true)
      else setShowingChat(!!selectedPackId)
    }
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [selectedPackId])

  // 1. Busca a lista de conversas ativas (cards laterais)
  const fetchConversations = useCallback(async () => {
    setIsLoading(true)
    try {
      const resp = await apinode.get(`/ml/messages/${localStorage.idconta}`)
      const data = resp.data?.conversas || resp.data || []
      setConversations(data)

      // Seleciona a primeira conversa por padrão se existir
      if (data.length > 0 && !selectedPackId) {
        setSelectedPackId(data[0].pack_id)
      }
    } catch (error) {
      console.error('Erro ao buscar lista de conversas:', error)
    } finally {
      setIsLoading(false)
    }
  }, [selectedPackId])

  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  // 2. Busca todas as mensagens do pack_id selecionado
  const fetchPackMessages = useCallback(async (packId) => {
    if (!packId) return

    try {
      const resp = await apinode.get(
        `/ml/messages/${packId}/${localStorage.idconta}`,
      )

      setMessages(resp.data?.messages || resp.data || [])
    } catch (error) {
      console.error('Erro ao buscar historico do pack:', error)
    }
  }, [])

  useEffect(() => {
    if (selectedPackId) {
      fetchPackMessages(selectedPackId)
    }
  }, [selectedPackId, fetchPackMessages])

  // Filtro de pesquisa lateral
  const filteredConversations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return conversations
    return conversations.filter(
      (c) =>
        c.pack_id?.toLowerCase().includes(q) ||
        c.ultima_mensagem?.toLowerCase().includes(q) ||
        c.ultimo_remetente?.toLowerCase().includes(q),
    )
  }, [conversations, searchTerm])

  const selectedConversation = useMemo(() => {
    return conversations.find((c) => c.pack_id === selectedPackId) || null
  }, [conversations, selectedPackId])

  // Enviar Mensagem Pós-Venda
  const sendResponse = async () => {
    const textToSend = response.trim()
    if (!selectedPackId || !textToSend) return

    const sellerId = selectedConversation?.seller_id || localStorage.idconta

    // Mensagem Otimista na Interface
    const tempMsg = {
      id: `temp-${Date.now()}`,
      pack_id: selectedPackId,
      message_de: String(sellerId),
      mensagem: textToSend,
      data_criacao: new Date().toISOString(),
      isMine: true,
    }

    setMessages((prev) => [...prev, tempMsg])
    setResponse('')

    try {
      await apinode.post(
        `/mercadolibreMercadoLivreMessagesresposta/${selectedPackId}/${sellerId}/${localStorage.idconta}`,
        { text: textToSend },
      )
      // Atualiza mensagens reais do banco
      fetchPackMessages(selectedPackId)
    } catch (error) {
      console.error(error)
      swal('Erro', 'Não foi possível enviar a mensagem', 'warning')
      fetchPackMessages(selectedPackId)
    }
  }

  const activePalette = accountPalette(
    selectedConversation?.seller_id || localStorage.idconta,
  )

  return (
    <>
      <GlobalStyles />

      <Navbar style={S.container} />

      {conversations.length === 0 ? (
        <div style={S.page(isMobile)}>
          <div style={S.emptyState}>
            <Lottie
              animationData={productNotFoundLottie}
              style={{ width: 260, height: 260 }}
            />
            <h2 style={S.emptyTitle}>Nenhuma conversa encontrada</h2>
            <Link
              to="/home"
              style={S.emptyLink}
            >
              Voltar para o início
            </Link>
          </div>
        </div>
      ) : (
        <div style={S.page(isMobile)}>
          {/* Sidebar */}
          <div style={S.sidebarWrap(isMobile, showingChat)}>
            <div style={S.sidebarHeader}>
              <div>
                <h3 style={S.sidebarTitle}>Mensagens</h3>
                <div style={S.sidebarSubtitle}>
                  {filteredConversations.length} conversas
                </div>
              </div>
              <div style={S.accountInfo}>
                <div style={S.accountInfoAccent}>
                  Conta: {localStorage.idconta}
                </div>
              </div>
            </div>

            <div style={S.searchWrap}>
              <div style={S.searchInputWrap}>
                <Search
                  size={16}
                  style={S.searchIcon}
                />
                <input
                  type="text"
                  placeholder="Buscar por pacote ou texto..."
                  className="pgt-search-input"
                  style={S.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div
              className="pgt-list-scroll"
              style={S.chatList}
            >
              {filteredConversations.map((c) => {
                const palette = accountPalette(c.seller_id)
                const isSelected = selectedPackId === c.pack_id
                return (
                  <div
                    key={c.pack_id}
                    className="pgt-chat-item"
                    style={S.chatItem(isSelected)}
                    onClick={() => {
                      setSelectedPackId(c.pack_id)
                      if (isMobile) setShowingChat(true)
                    }}
                  >
                    <div style={S.avatar(palette)}>
                      {initialsOf(c.ultimo_remetente || c.pack_id)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={S.itemTitleRow}>
                        <div style={S.itemTitle}>Pacote: {c.pack_id}</div>
                        <div style={S.itemTime}>
                          {c.data_ultima_mensagem
                            ? formatDistanceToNow(
                                new Date(c.data_ultima_mensagem),
                                { locale },
                              )
                            : ''}
                        </div>
                      </div>
                      <div style={S.itemChips}>
                        <span style={S.chatBadge(palette)}>
                          Conta {c.seller_id}
                        </span>
                      </div>
                      <div style={S.itemLastMsg}>{c.ultima_mensagem}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Chat Principal */}
          <ContentLoading isLoading={isLoading}>
            <div style={S.chatPane(isMobile, showingChat)}>
              {selectedConversation ? (
                <div style={S.chatHeader}>
                  {isMobile && (
                    <button
                      className="pgt-back-btn"
                      style={S.backBtn}
                      onClick={() => setShowingChat(false)}
                    >
                      <ArrowLeft size={17} />
                    </button>
                  )}
                  <div style={S.avatar(activePalette)}>
                    {initialsOf(selectedConversation.pack_id)}
                  </div>
                  <div style={S.headerTitleWrap}>
                    <div style={S.chatHeaderTitle}>
                      Pacote: {selectedConversation.pack_id}
                    </div>
                    <div style={S.chatHeaderSub}>
                      <span style={S.chatBadge(activePalette)}>
                        Venda Pós-Venda
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={S.chatHeader}>
                  <div style={{ color: T.textMuted, fontSize: 13.5 }}>
                    Selecione uma conversa
                  </div>
                </div>
              )}

              {/* Balões do Chat */}
              <div
                className="pgt-messages-scroll"
                style={S.messagesWrap}
              >
                {messages.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      margin: 'auto',
                      color: T.textSec,
                    }}
                  >
                    <Lottie
                      animationData={animacaoLottie}
                      style={{ width: 180, height: 180 }}
                    />
                    <p style={{ fontSize: 13.5 }}>Carregando mensagens...</p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => {
                      // Define se a mensagem foi enviada pela sua conta ou pelo comprador
                      const isMine =
                        msg.isMine ||
                        String(msg.message_de) ===
                          String(selectedConversation?.seller_id) ||
                        String(msg.message_de) === String(localStorage.idconta)
                      const time = msg.data_criacao
                        ? formatDistanceToNow(new Date(msg.data_criacao), {
                            locale,
                          })
                        : ''

                      return (
                        <div
                          key={msg.id || msg.mensagem_id}
                          style={S.bubbleRow(isMine)}
                        >
                          <div style={S.bubble(isMine)}>
                            {!isMine && (
                              <div style={S.bubbleAuthor(isMine)}>
                                Comprador ({msg.message_de})
                              </div>
                            )}
                            <div>{msg.mensagem}</div>
                            <div style={S.bubbleMeta(isMine)}>
                              <Clock size={10} />
                              {time} atrás
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input Bar */}
              <div style={S.inputBar}>
                <input
                  type="text"
                  placeholder="Digite sua resposta para o comprador..."
                  className="pgt-msg-input"
                  style={S.input}
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      sendResponse()
                    }
                  }}
                />
                <button
                  className="pgt-send-btn"
                  style={{
                    ...S.sendBtn,
                    ...(response?.trim() ? {} : S.sendBtnDisabled),
                  }}
                  onClick={sendResponse}
                  disabled={!response?.trim()}
                >
                  <SendHorizontal size={16} />
                  Enviar
                </button>
              </div>
            </div>
          </ContentLoading>
        </div>
      )}
    </>
  )
}

export default MercadoLivreMessages
